import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations using Intersection Observer
 * @param {Object} options - Intersection Observer options
 * @param {number} options.threshold - Visibility threshold (0-1), default 0.1
 * @param {string} options.rootMargin - Root margin, default "0px 0px -50px 0px"
 * @returns {Object} { ref, isVisible }
 */
export function useScrollAnimation(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  const { threshold = 0.1, rootMargin = "0px 0px -50px 0px" } = options;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element); // Only animate once
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return { ref, isVisible };
}

/**
 * Custom hook that initializes scroll animations for all elements with scroll-animate classes
 * Call this once at the top level of a page component
 */
export function useInitScrollAnimations() {
  useEffect(() => {
    // Small delay to ensure dynamic content is rendered
    const timeoutId = setTimeout(() => {
      const animatedElements = document.querySelectorAll(
        '.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale, .scroll-card, .stat-animate, .image-reveal'
      );

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05, rootMargin: "0px 0px -20px 0px" }
      );

      animatedElements.forEach((el) => observer.observe(el));

      return () => observer.disconnect();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Re-run when DOM changes (for dynamically loaded content)
  useEffect(() => {
    const mutationObserver = new MutationObserver(() => {
      const animatedElements = document.querySelectorAll(
        '.scroll-animate:not(.animate-in), .scroll-animate-left:not(.animate-in), .scroll-animate-right:not(.animate-in), .scroll-animate-scale:not(.animate-in), .scroll-card:not(.animate-in), .stat-animate:not(.animate-in), .image-reveal:not(.animate-in)'
      );
      
      if (animatedElements.length === 0) return;
      
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
              intersectionObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05, rootMargin: "0px 0px -20px 0px" }
      );

      animatedElements.forEach((el) => intersectionObserver.observe(el));
    });
    
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    
    return () => mutationObserver.disconnect();
  }, []);
}

/**
 * ScrollAnimated component wrapper - easier to use in JSX
 */
export function ScrollAnimated({ 
  children, 
  animation = 'fade-up', // fade-up, fade-left, fade-right, scale, card, stat
  delay = 0,
  className = '',
  ...props 
}) {
  const { ref, isVisible } = useScrollAnimation();
  
  const animationClasses = {
    'fade-up': 'scroll-animate',
    'fade-left': 'scroll-animate-left',
    'fade-right': 'scroll-animate-right',
    'scale': 'scroll-animate-scale',
    'card': 'scroll-card',
    'stat': 'stat-animate',
    'image': 'image-reveal'
  };
  
  const baseClass = animationClasses[animation] || 'scroll-animate';
  
  return (
    <div
      ref={ref}
      className={`${baseClass} ${isVisible ? 'animate-in' : ''} ${className}`}
      style={{ transitionDelay: delay ? `${delay}s` : undefined }}
      {...props}
    >
      {children}
    </div>
  );
}

export default useScrollAnimation;
